import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import BasicCheckupComponent from "./BasicCheckupComponent";
import FinishPrescriptionButton from "./FinishPrescriptionButton/index";
import FollowUpComponent from "./FollowUpComponent/index.tsx";
import FoodAndDietSection from "./FoodAndDietSection/index.tsx";
import DewormingSection from "./DewormingSection/index.tsx";
import GeneralAppearanceComponent from "./GeneralAppearanceComponent";
import HealthScoreDetailsComponent from "./HealthScoreDetailsComponent";
import ImageAttachmentComponent from "./ImageAttachmentComponent";
import LabTestDiagnosticsComponent from "./LabTestDiagnosticsComponent/index.tsx";
import MedicalInstructionsComponent from "./MedicalInstructionsComponent";
import MedicinesComponent from "./MedicinesComponent/index.tsx";
import MedicinesSectionV2 from "./MedicinesComponent/MedicinesSectionV2.tsx";
import NavigationComponent from "./NavigationComponent";
import NextStepsComponent from "./NextStepsComponent/index.tsx";
import ReferToOtherClinicComponent, { hasLegacyNextSteps } from "./ReferToOtherClinicComponent/index.tsx";
import GroomingVisitComponent from "./GroomingVisitComponent/index.tsx";
import PetProfileComponent from "./PetProfileComponent";
import TemplateTypeComponent from "./TemplateTypeComponent";
import TreatmentsComponent from "./TreatmentsComponent/";
import VitalsComponent from "./VitalsComponent";
import ReasonForVisitComponent from "./ReasonForVisitComponent";
import VoiceEMRRecorder from "./VoiceEMRRecorder";
import { IEMRUpdatePayloadProps } from "@/api_framework/api_modals/EMRs.ts";
import { useEMRDetailsAPI } from "@/app/hooks/api_hooks/emr/useEMRDetailsAPI.ts";
import { BLUE7_BORIVALI_CLINIC_ID } from "@/constants";
import { useEMRClinicConfigAPI, IEMRClinicConfig } from "@/app/hooks/api_hooks/emr/useEMRClinicConfigAPI.ts";
import MedicineHistoryTab from "../CustomerProfile/CustomerDetails/Headers/MedicineHistoryTab/index.tsx";
import NudgesContainer from "@/app/components/ui/Nudges/NudgesContainer/index.tsx";
import {
  INudgesActionOnFoodMedicineProps,
  useFoodandMedicineAddOnNudgeActionAPI,
} from "@/app/hooks/api_hooks/nudges/useFoodandMedicineAddOnNudgeActionAPI.ts";
import { useAppDispatch, useAppSelector } from "@/app_redux/hooks/root_hook.ts";
import PrognosisComponent from "./PrognosisComponent/index.tsx";
import { useCustomerDetailsAPI } from "@/app/hooks/api_hooks/customer_profile/useCustomerDetailsAPI.ts";
import { setCustomerId, setPetId } from "@/app_redux/reducers/slices/auth/customer_profile_slice.ts";

const EMRTemplateCreationPage = () => {
  const [emrDetails, setEmrDetails] = useState<IEMRUpdatePayloadProps | null>(null);
  const [showMedicalHistory, setShowMedicalHistory] = useState<boolean>(false);
  const [hasShownNudges, setHasShownNudges] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isBilable, setIsBilable] = useState<boolean>(false);
  const [clinicConfig, setClinicConfig] = useState<IEMRClinicConfig | null>(null);
  const [isVoicePanelOpen, setIsVoicePanelOpen] = useState<boolean>(false);
  // Set when this tab was opened by the "Voice to EMR" button on the customer
  // profile: the panel opens and starts recording without a second tap.
  const [autoStartVoice, setAutoStartVoice] = useState<boolean>(false);
  const [voiceDuration, setVoiceDuration] = useState<number>(0);

  const dispatch = useAppDispatch();
  const [initialLoad, setInitialLoad] = useState<boolean>(false);

  const petProfileRef = useRef<HTMLDivElement>(null);
  const healthScoreRef = useRef<HTMLDivElement>(null);
  const prognosisRef = useRef<HTMLDivElement>(null);
  const generalAppearanceRef = useRef<HTMLDivElement>(null);

  const userRole: any = useAppSelector((state) => state?.auth?.userPermissions ?? state?.auth?.userRole);

  const { execute: addFoodAndMedicineOnNudgeAction } = useFoodandMedicineAddOnNudgeActionAPI();
  const fetchEMRDetailsAPI = useEMRDetailsAPI();
  const fetchCustomerDetails = useCustomerDetailsAPI();
  const { execute: fetchClinicConfig } = useEMRClinicConfigAPI();

  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");
  const shouldAutoStartVoice = params.get("voice") === "1";

  const updateEmrDetailsState = (nextDetails: IEMRUpdatePayloadProps | null) => {
    if (!nextDetails) return;

    // Preserve existing fields (like high_risk_patient) when update responses omit them
    setEmrDetails((prev) => {
      if (!prev) return nextDetails;

      return {
        ...prev,
        ...nextDetails,
        high_risk_patient: nextDetails.high_risk_patient ?? prev.high_risk_patient,
        deworming_meds_treatments: nextDetails.deworming_meds_treatments ?? prev.deworming_meds_treatments,
        grooming_visit: nextDetails.grooming_visit,
        pet_details: {
          ...(prev.pet_details ?? {}),
          ...(nextDetails.pet_details ?? {}),
        },
        customer_details: {
          ...(prev.customer_details ?? {}),
          ...(nextDetails.customer_details ?? {}),
        },
      };
    });
  };

  const handelFetchEMRData = () => {
    fetchEMRDetailsAPI(emr_id).then(({ status, data }) => {
      if (status) {
        updateEmrDetailsState(data);
      }
    });
  };

  useEffect(() => {
    if (emr_id) {
      handelFetchEMRData();
    }
  }, [emr_id]);

  // Fetch clinic config when emrDetails loads with visited_clinic_id
  useEffect(() => {
    const clinicId = emrDetails?.visited_clinic_id;
    if (clinicId && !clinicConfig) {
      fetchClinicConfig(clinicId).then((cfg) => {
        if (cfg) setClinicConfig(cfg);
      });
    }
  }, [emrDetails?.visited_clinic_id]);

  useEffect(() => {
    if (userRole) {
      const wasShown = localStorage.getItem("nudgesShown_emr");
      const shouldShow = wasShown === null || wasShown === "false";
      if (shouldShow) {
        setHasShownNudges(true);
        localStorage.setItem("nudgesShown_emr", "true");
      }
    }
  }, [userRole]);

  const alert_category_list = [
    "high_risk_emr",
    "New User",
    "Vaccination",
    "Vaccination Addon",
    "Deworming",
    "Upsell / Re-Sell Medicine",
    "Upsell / Re-Sell V Diet",
    "Draft Check",
  ];

  const handelAddFoodAndMedicine = (nudge: any) => {
    const constructedBody: INudgesActionOnFoodMedicineProps = {
      customer_id: emrDetails?.customer_details?.customer_id,
      pet_id: emrDetails?.pet_details?.pet_id,
      emr_id: emr_id,
      food_medicine_type: nudge?.food_medicine_type,
      nudge_id: nudge?.nudge_id,
      roll_id: userRole?.role_id,
    };
    addFoodAndMedicineOnNudgeAction(constructedBody).then(({ status }) => {
      if (status) {
        handelFetchEMRData();
        return { status: true };
      }
    });
  };

  const fieldSectionMap: Record<string, any> = {
    Temperature: petProfileRef,
    Weight: petProfileRef,
    "Body Condition Score": healthScoreRef,
    "Dental Score": healthScoreRef,
    Prognosis: prognosisRef,
    "General Appearance": generalAppearanceRef,
    Appetite: generalAppearanceRef,
    "CMM": generalAppearanceRef,
    Ears: generalAppearanceRef,
    Paws: generalAppearanceRef,
    Body: generalAppearanceRef,
    Skin: generalAppearanceRef,
    "Lymph Nodes": generalAppearanceRef,
  };

  const scrollToField = (field: string) => {
    const ref = fieldSectionMap[field];

    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  useEffect(() => {
    if (emrDetails && !initialLoad) {
      const customerId = emrDetails?.customer_details?.customer_id;
      const pet_id = emrDetails?.pet_details?.pet_id;
      dispatch(
        setPetId({
          petId: pet_id,
        }),
      );
      dispatch(
        setCustomerId({
          customerId: customerId,
        }),
      );
      fetchCustomerDetails(customerId);

      setInitialLoad(true);
      document.title = emrDetails?.pet_details?.pet_name;
    }
  }, [emrDetails]);

  const shouldRenderMedicinesSectionV2 = emrDetails?.medicines?.sell_med_live === true;

  // The panel opens and recording arms itself on every EMR page. ?voice=1 only
  // marks the Voice-to-EMR entry points, whose flags are stripped after use.
  useEffect(() => {
    if (!emr_id || isBilable) return;
    setIsVoicePanelOpen(true);
    // Recording arms itself on every EMR page, not only the ?voice=1 entry points.
    setAutoStartVoice(true);
    if (!shouldAutoStartVoice) return;
    // One-shot: drop the flag from the URL so a refresh doesn't start a second
    // recording, and closing the panel doesn't immediately reopen it.
    const next = new URLSearchParams(params);
    next.delete("voice");
    window.history.replaceState({}, "", `${window.location.pathname}?${next.toString()}`);
  }, [shouldAutoStartVoice, emr_id, isBilable]);

  // Opening the panel — from the nav pill or anywhere else — also arms auto-start,
  // so the doctor goes straight to recording instead of opening and then tapping
  // the mic. Accepts the updater form because the nav toggle uses `(prev) => !prev`.
  const toggleVoicePanel: Dispatch<SetStateAction<boolean>> = (value) => {
    const next = typeof value === "function" ? (value as (prev: boolean) => boolean)(isVoicePanelOpen) : value;
    setIsVoicePanelOpen(next);
    if (next) setAutoStartVoice(true);
  };


  const isPrognosBeforeFollowUp = emrDetails?.visited_clinic_id === BLUE7_BORIVALI_CLINIC_ID;
  const isVideoConsultation = emrDetails?.emr_type === "Video Consultation";

  const prognosisSection = !isBilable && (
    <div ref={prognosisRef}>
      <PrognosisComponent
        emrDetails={emrDetails}
        setEmrDetails={updateEmrDetailsState}
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
      />
    </div>
  );

  return (
    <div>
      <NavigationComponent
        setShowMedicalHistory={setShowMedicalHistory}
        emrDetails={emrDetails}
        setShowVoicePanel={toggleVoicePanel}
        isVoicePanelOpen={isVoicePanelOpen}
        voiceDuration={voiceDuration}
      />
      <div className="">
        {showMedicalHistory && <MedicineHistoryTab isDefaultShow={true} emrDetails={emrDetails} />}
        {!isBilable && (
          <VoiceEMRRecorder
            emrId={emr_id}
            emrStatus={emrDetails?.emr_status}
            emrDetails={emrDetails}
            onSuccess={(data) => updateEmrDetailsState(data)}
            isOpen={isVoicePanelOpen}
            // Restore must NOT go through toggleVoicePanel — that arms auto-start,
            // and coming back to a running session has nothing to start.
            onRestore={() => setIsVoicePanelOpen(true)}
            autoStart={autoStartVoice}
            fullHeight
            onAutoStartConsumed={() => setAutoStartVoice(false)}
            onDurationChange={setVoiceDuration}
            onClose={() => setIsVoicePanelOpen(false)}
          />
        )}
        <TemplateTypeComponent
          emrDetails={emrDetails}
          setEmrDetails={updateEmrDetailsState}
          setIsBilable={setIsBilable}
          isBilable={isBilable}
        />

        <div ref={petProfileRef} data-voice-section="pet-profile">
          <PetProfileComponent
            emrDetails={emrDetails}
            setEmrDetails={updateEmrDetailsState}
            fetchEMRDetailsAPI={handelFetchEMRData}
            errorMessage={errorMessage}
            setErrorMessage={setErrorMessage}
          />
        </div>
        {!isBilable && !isVideoConsultation && clinicConfig && (clinicConfig.show_basic_checkup ?? true) && (
          <div data-voice-section="basic-checkup">
            <BasicCheckupComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
          </div>
        )}
        {!isBilable && clinicConfig?.vitals_enabled && (
          <VitalsComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} clinicConfig={clinicConfig} />
        )}
        {!isBilable && clinicConfig?.vitals_enabled && emrDetails?.reason_for_visit !== undefined && (
          <ReasonForVisitComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
        )}
        {!isBilable && !isVideoConsultation && clinicConfig && (clinicConfig.show_basic_checkup ?? true) && (
          <div ref={healthScoreRef}>
            <HealthScoreDetailsComponent
              emrDetails={emrDetails}
              setEmrDetails={updateEmrDetailsState}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
            />
          </div>
        )}
        {!isPrognosBeforeFollowUp && prognosisSection}
        {emrDetails?.emr_type === "Post Grooming Consultation" && !isBilable && (
          <div ref={generalAppearanceRef}>
            <GeneralAppearanceComponent
              emrDetails={emrDetails}
              setEmrDetails={updateEmrDetailsState}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
            />
          </div>
        )}
        {!isBilable && (
          <div data-voice-section="medical-instructions">
            <MedicalInstructionsComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
          </div>
        )}
        <div data-voice-section="lab-tests">
          <LabTestDiagnosticsComponent
            emrDetails={emrDetails}
            setEmrDetails={updateEmrDetailsState}
            isVideoConsultation={isVideoConsultation}
          />
        </div>
        <div data-voice-section="deworming">
          <DewormingSection emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
        </div>
        {!isVideoConsultation && (
          <div data-voice-section="treatments">
            <TreatmentsComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
          </div>
        )}
        <div data-voice-section="medicines">
          {shouldRenderMedicinesSectionV2 ? (
            <MedicinesSectionV2 emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
          ) : (
            <MedicinesComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
          )}
        </div>
        <div data-voice-section="food-and-diet">
          <FoodAndDietSection emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
        </div>
        {isPrognosBeforeFollowUp && prognosisSection}
        <div data-voice-section="follow-up">
          <FollowUpComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} isBilable={isBilable} />
        </div>
        {!isBilable && emrDetails?.grooming_visit && (
          <GroomingVisitComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
        )}
        {!isBilable &&
          (emrDetails?.emr_type === "Video Consultation" || hasLegacyNextSteps(emrDetails?.next_steps) ? (
            <NextStepsComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
          ) : (
            <ReferToOtherClinicComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />
          ))}
        {!isBilable && <ImageAttachmentComponent emrDetails={emrDetails} setEmrDetails={updateEmrDetailsState} />}
        <div className="mb-20">
          <FinishPrescriptionButton
            emrDetails={emrDetails}
            setErrorMessage={setErrorMessage}
            scrollToField={scrollToField}
            isBilable={isBilable}
            setIsBilable={setIsBilable}
          />
        </div>
        {hasShownNudges && (
          <NudgesContainer
            source="EMR"
            ermid={emr_id}
            alert_category_list={alert_category_list}
            handelAddFoodAndMedicine={handelAddFoodAndMedicine}
          />
        )}
      </div>
    </div>
  );
};
export default EMRTemplateCreationPage;
